import { createTheme } from '@mui/material';
import { alertStyles } from './alert';
import { shadowStyles } from './boxShadows';
import { buttonStyles } from './buttons';
import { chipStyles } from './chips';
import { colors } from './colors';
import { formStyles } from './form';
import { listStyles } from './list';
import { skeletonStyles } from './skeleton';
import { fonts, typographyStyles } from './typography';

let theme = createTheme({
  spacing: (factor: number) => `${0.5 * factor}rem`,
  palette: {
    ...colors,
    divider: colors.grey[200],
    tonalOffset: 0,
  },
  typography: {
    fontFamily: fonts.body,
    fontWeightBold: 700,
    fontWeightMedium: 600,
    fontWeightRegular: 400,
    button: {
      textTransform: 'none',
    },
  },

  breakpoints: {
    unit: 'em',
    values: {
      xs: 0,
      sm: 37.5,
      md: 56.25,
      lg: 75,
      xl: 96,
    },
  },

  components: {
    // Dialog
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '1rem',
        },
        scrollPaper: {
          '.MuiDialogContent-root': {
            paddingBottom: 0,
          },
          '.MuiDialogActions-root': {
            padding: '1rem 1.5rem',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '1.5rem',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '1.5rem',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0 1.5rem 1rem',
        },
      },
    },
    // Typography Overrides
    MuiTypography: {
      variants: [
        {
          props: { fontWeight: 'medium' },
          style: {
            fontWeight: 600,
          },
        },
      ],
    },

    // Buttons Components Overrides
    MuiButtonBase: {
      styleOverrides: {
        root: {
          padding: '.75rem 1.125rem',
        },
      },
    },

    // Form Fields
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '0.25rem',
          paddingRight: '1.125rem',
          fontSize: '0.875rem',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          marginTop: '0.25rem',
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          paddingRight: '1.125rem',
          ':before': {
            display: 'none',
          },
          ':after': {
            display: 'none',
          },
        },
        input: {
          paddingLeft: '1.125rem',
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          paddingRight: '1.125rem',
        },
        input: {
          paddingLeft: '1.125rem',
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
          boxSizing: 'border-box',
          height: '3rem',
        },
        inputSizeSmall: {
          paddingLeft: '1rem',
          paddingTop: '0.5rem',
          paddingBottom: '0.5rem',
          height: '2.5rem',
        },
        notchedOutline: {
          borderColor: colors.grey[200],
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '.875rem',
          marginBottom: '0.375rem',
        },
        asterisk: {
          color: colors.error.main,
        },
      },
    },

    // Skeleton
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
    },
  },
});

theme = createTheme(
  theme,
  typographyStyles(theme),
  shadowStyles(theme),
  buttonStyles(theme),
  listStyles(theme),
  skeletonStyles(theme),
  alertStyles(theme),
  formStyles(theme),
  chipStyles(theme)
);

export { theme };
